import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from '../../services/supabase';

import {
    Overlay,
    Modal,
    ModalInner,
    Title,
    Field,
    PrimaryButton,
    SecondaryButton,
    UserEmail,
    LinkButton,
    ErrorText,
    SuccessText,
    TextLink,
    CloseButton,
    SubtitleText,
} from './style.css.js';

const DEMO_EMAIL = 'hello@macarons-shop.site';
const DEMO_PASSWORD = 'testtest2';

const AuthModal = ({ isOpen, onClose, user, onLogin, onLogout }) => {
    const isDark = true;
    const navigate = useNavigate();
    const [email, setEmail] = useState(DEMO_EMAIL);
    const [password, setPassword] = useState(DEMO_PASSWORD);
    const [confirmPassword, setConfirmPassword] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [forgotPassword, setForgotPassword] = useState(false);
    const [resetEmailSent, setResetEmailSent] = useState(false);
    const [isSignUp, setIsSignUp] = useState(false);
    const [signUpSuccess, setSignUpSuccess] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        if (!email.trim()) {
            setError('Veuillez saisir votre adresse email.');
            return;
        }
        if (!password) {
            setError('Veuillez saisir votre mot de passe.');
            return;
        }
        setIsSubmitting(true);
        try {
            const { data, error: signInError } = await supabase.auth.signInWithPassword({
                email: email.trim(),
                password,
            });
            if (signInError) {
                throw new Error(signInError.message === 'Invalid login credentials'
                    ? 'Email ou mot de passe incorrect.'
                    : signInError.message);
            }
            if (data?.user) {
                onLogin({ id: data.user.id, email: data.user.email });
                onClose();
                navigate('/dashboard', { replace: true });
            } else {
                throw new Error('Connexion impossible.');
            }
        } catch (err) {
            setError(err.message || 'Une erreur est survenue.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleSignUp = async (e) => {
        e.preventDefault();
        setError('');
        if (!email.trim()) {
            setError('Veuillez saisir votre adresse email.');
            return;
        }
        if (!password) {
            setError('Veuillez saisir un mot de passe.');
            return;
        }
        if (password.length < 6) {
            setError('Le mot de passe doit contenir au moins 6 caractères.');
            return;
        }
        if (password !== confirmPassword) {
            setError('Les deux mots de passe ne correspondent pas.');
            return;
        }
        setIsSubmitting(true);
        setSignUpSuccess(false);
        try {
            const { data, error: signUpError } = await supabase.auth.signUp({
                email: email.trim(),
                password,
                options: { emailRedirectTo: `${window.location.origin}/dashboard` },
            });
            if (signUpError) throw new Error(signUpError.message);
            if (data?.user) {
                setSignUpSuccess(true);
                if (data.session) {
                    onLogin({ id: data.user.id, email: data.user.email });
                    onClose();
                    navigate('/dashboard', { replace: true });
                }
            }
        } catch (err) {
            setError(err.message || 'Une erreur est survenue lors de l\'inscription.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleForgotPassword = async (e) => {
        e.preventDefault();
        setError('');
        setResetEmailSent(false);
        if (!email.trim()) {
            setError('Veuillez saisir votre adresse email.');
            return;
        }
        setIsSubmitting(true);
        try {
            const redirectTo = `${window.location.origin}/reset-password`;
            const { error: resetError } = await supabase.auth.resetPasswordForEmail(email.trim(), { redirectTo });
            if (resetError) throw new Error(resetError.message);
            setResetEmailSent(true);
        } catch (err) {
            setError(err.message || 'Une erreur est survenue.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleLogout = () => {
        onLogout();
        onClose();
    };

    if (!isOpen) return null;

    return (
        <Overlay $dark={isDark} onClick={(e) => e.target === e.currentTarget && onClose()}>
            <Modal $dark={isDark} onClick={(e) => e.stopPropagation()}>
                <ModalInner>
                    <CloseButton type="button" $dark={isDark} onClick={onClose} aria-label="Fermer">
                        ×
                    </CloseButton>
                    {user ? (
                        <>
                            <Title $dark={isDark}>Mon compte</Title>
                            <UserEmail $dark={isDark}>{user.email}</UserEmail>
                            <LinkButton $dark={isDark} as={Link} to="/dashboard" onClick={onClose}>
                                Tableau de bord
                            </LinkButton>
                            <SecondaryButton type="button" $dark={isDark} onClick={handleLogout}>
                                Déconnexion
                            </SecondaryButton>
                        </>
                    ) : forgotPassword ? (
                        <>
                            <Title $dark={isDark}>Mot de passe oublié</Title>
                            <SubtitleText $dark={isDark}>
                                Saisissez votre email pour recevoir un lien de réinitialisation.
                            </SubtitleText>
                            {resetEmailSent ? (
                                <>
                                    <SuccessText $dark={isDark}>
                                        Si un compte existe pour cet email, un lien de réinitialisation vous a été envoyé. Vérifiez votre boîte de réception.
                                    </SuccessText>
                                    <SecondaryButton type="button" $dark={isDark} onClick={() => { setForgotPassword(false); setResetEmailSent(false); setEmail(DEMO_EMAIL); setPassword(DEMO_PASSWORD); }}>
                                        Retour à la connexion
                                    </SecondaryButton>
                                </>
                            ) : (
                                <form onSubmit={handleForgotPassword}>
                                    {error && <ErrorText $dark={isDark}>{error}</ErrorText>}
                                    <Field $dark={isDark}>
                                        <label htmlFor="auth-forgot-email">Email</label>
                                        <input
                                            id="auth-forgot-email"
                                            type="email"
                                            value={email}
                                            onChange={(e) => setEmail(e.target.value)}
                                            placeholder="vous@exemple.com"
                                            autoComplete="email"
                                            disabled={isSubmitting}
                                        />
                                    </Field>
                                    <PrimaryButton type="submit" $dark={isDark} disabled={isSubmitting}>
                                        {isSubmitting ? 'Envoi...' : 'Envoyer le lien'}
                                    </PrimaryButton>
                                    <TextLink type="button" $dark={isDark} onClick={() => { setForgotPassword(false); setError(''); setEmail(DEMO_EMAIL); setPassword(DEMO_PASSWORD); }}>
                                        Retour à la connexion
                                    </TextLink>
                                </form>
                            )}
                        </>
                    ) : isSignUp ? (
                        <>
                            <Title $dark={isDark}>Créer un compte</Title>
                            <SubtitleText $dark={isDark}>
                                Saisissez votre email et un mot de passe (6 caractères minimum) pour créer votre compte.
                            </SubtitleText>
                            {signUpSuccess ? (
                                <>
                                    <SuccessText $dark={isDark}>
                                        Compte créé. Si la confirmation par email est activée, vérifiez votre boîte de réception pour activer votre compte. Sinon vous pouvez vous connecter.
                                    </SuccessText>
                                    <SecondaryButton type="button" $dark={isDark} onClick={() => { setIsSignUp(false); setSignUpSuccess(false); setError(''); }}>
                                        Retour à la connexion
                                    </SecondaryButton>
                                </>
                            ) : (
                                <form onSubmit={handleSignUp}>
                                    {error && <ErrorText $dark={isDark}>{error}</ErrorText>}
                                    <Field $dark={isDark}>
                                        <label htmlFor="auth-signup-email">Email</label>
                                        <input
                                            id="auth-signup-email"
                                            type="email"
                                            value={email}
                                            onChange={(e) => setEmail(e.target.value)}
                                            placeholder="vous@exemple.com"
                                            autoComplete="email"
                                            disabled={isSubmitting}
                                        />
                                    </Field>
                                    <Field $dark={isDark}>
                                        <label htmlFor="auth-signup-password">Mot de passe</label>
                                        <input
                                            id="auth-signup-password"
                                            type="password"
                                            value={password}
                                            onChange={(e) => setPassword(e.target.value)}
                                            placeholder="••••••••"
                                            autoComplete="new-password"
                                            disabled={isSubmitting}
                                        />
                                    </Field>
                                    <Field $dark={isDark}>
                                        <label htmlFor="auth-signup-confirm">Confirmer le mot de passe</label>
                                        <input
                                            id="auth-signup-confirm"
                                            type="password"
                                            value={confirmPassword}
                                            onChange={(e) => setConfirmPassword(e.target.value)}
                                            placeholder="••••••••"
                                            autoComplete="new-password"
                                            disabled={isSubmitting}
                                        />
                                    </Field>
                                    <PrimaryButton type="submit" $dark={isDark} disabled={isSubmitting}>
                                        {isSubmitting ? 'Inscription...' : 'S\'inscrire'}
                                    </PrimaryButton>
                                    <TextLink type="button" $dark={isDark} onClick={() => { setIsSignUp(false); setError(''); setConfirmPassword(''); }}>
                                        Déjà un compte ? Se connecter
                                    </TextLink>
                                </form>
                            )}
                        </>
                    ) : (
                        <>
                            <Title $dark={isDark}>Connexion</Title>
                            <SubtitleText $dark={isDark}>
                                Saisissez votre email et mot de passe pour accéder à votre tableau de bord.
                            </SubtitleText>
                            <form onSubmit={handleSubmit}>
                                {error && <ErrorText $dark={isDark}>{error}</ErrorText>}
                                <Field $dark={isDark}>
                                    <label htmlFor="auth-email">Email</label>
                                    <input
                                        id="auth-email"
                                        type="email"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        placeholder="vous@exemple.com"
                                        autoComplete="email"
                                        disabled={isSubmitting}
                                    />
                                </Field>
                                <Field $dark={isDark}>
                                    <label htmlFor="auth-password">Mot de passe</label>
                                    <input
                                        id="auth-password"
                                        type="password"
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        placeholder="••••••••"
                                        autoComplete="current-password"
                                        disabled={isSubmitting}
                                    />
                                </Field>
                                <PrimaryButton type="submit" $dark={isDark} disabled={isSubmitting}>
                                    {isSubmitting ? 'Connexion...' : 'Se connecter'}
                                </PrimaryButton>
                                <TextLink type="button" $dark={isDark} onClick={() => { setForgotPassword(true); setError(''); }}>
                                    Mot de passe oublié ?
                                </TextLink>
                                <TextLink type="button" $dark={isDark} onClick={() => { setIsSignUp(true); setError(''); setConfirmPassword(''); }} style={{ display: 'block', marginTop: 12 }}>
                                    Pas encore de compte ? S'inscrire
                                </TextLink>
                            </form>
                        </>
                    )}
                </ModalInner>
            </Modal>
        </Overlay>
    );
};

export default AuthModal;
